import React, { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { getDashboardPath } from '../lib/navigation';
import Logo from './Logo';

/**
 * Left sidebar for subscription, billing, and add-ons — matches My Subscription shell.
 */
const SubscriptionSidebarLayout = ({ activeItem, children }) => {
	const navigate = useNavigate();
	const { tenantId } = useParams();
	const { user } = useAuth();
	const [sidebarCollapsed, setSidebarCollapsed] = useState(false);

	const tenant = tenantId || user?.tenantIdentifier || 'default';
	const dashboardPath = getDashboardPath(tenant, user?.role);

	const navBtn = (id, isActive) =>
		isActive
			? `w-full flex items-center ${sidebarCollapsed ? 'justify-center px-0 py-3' : 'space-x-3 px-4 py-3'} rounded-xl bg-gradient-to-r from-emerald-600 to-teal-600 text-white shadow-lg shadow-emerald-600/30`
			: `w-full flex items-center ${sidebarCollapsed ? 'justify-center px-0 py-3' : 'space-x-3 px-4 py-3'} rounded-xl text-gray-600 hover:bg-gray-100 transition-colors`;

	return (
		<div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-emerald-50/30 flex">
			<aside
				className={`${sidebarCollapsed ? 'w-20' : 'w-72'} bg-white border-r border-gray-200 flex flex-col fixed h-full shadow-sm z-30 transition-all duration-300 ease-in-out`}
			>
				<div className={`border-b border-gray-100 flex items-center ${sidebarCollapsed ? 'p-3 justify-center' : 'p-6'}`}>
					<Logo width={40} height={40} showText={!sidebarCollapsed} className="[&_span]:!text-xl" />
				</div>
				<nav className="flex-1 p-4 space-y-2">
					<button
						type="button"
						onClick={() => navigate(dashboardPath)}
						title="Back to Dashboard"
						className={navBtn('dashboard', false)}
					>
						<svg className="w-5 h-5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
							<path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 19l-7-7 7-7" />
						</svg>
						{!sidebarCollapsed && <span className="font-medium">Dashboard</span>}
					</button>
					<button
						type="button"
						onClick={() => navigate(`/${tenant}/my-subscription`)}
						title="My Subscription"
						className={navBtn('subscription', activeItem === 'subscription')}
					>
						<svg className="w-5 h-5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
							<path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z" />
						</svg>
						{!sidebarCollapsed && <span className="font-medium">My Subscription</span>}
					</button>
					<button
						type="button"
						onClick={() => navigate(`/${tenant}/billing`)}
						title="Billing"
						className={navBtn('billing', activeItem === 'billing')}
					>
						<svg className="w-5 h-5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
							<path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 7h6m0 10v-3m-3 3h.01M9 17h.01M9 14h.01M12 14h.01M15 11h.01M12 11h.01M9 11h.01M7 21h10a2 2 0 002-2V5a2 2 0 00-2-2H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
						</svg>
						{!sidebarCollapsed && <span className="font-medium">Billing</span>}
					</button>
					<button
						type="button"
						onClick={() => navigate(`/${tenant}/purchase-addon`)}
						title="Purchase Add-ons"
						className={navBtn('addons', activeItem === 'addons')}
					>
						<svg className="w-5 h-5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
							<path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 4v16m8-8H4" />
						</svg>
						{!sidebarCollapsed && <span className="font-medium">Purchase Add-ons</span>}
					</button>
				</nav>
				<div className={`border-t border-gray-100 ${sidebarCollapsed ? 'p-2' : 'p-4'}`}>
					<button
						type="button"
						onClick={() => setSidebarCollapsed((c) => !c)}
						title={sidebarCollapsed ? 'Expand menu' : 'Collapse menu'}
						className={`w-full flex items-center ${sidebarCollapsed ? 'justify-center px-0 py-2.5' : 'space-x-3 px-4 py-2.5'} rounded-lg text-gray-500 hover:bg-gray-100 transition-colors`}
					>
						<svg className={`w-5 h-5 transition-transform duration-300 ${sidebarCollapsed ? '' : 'rotate-180'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
							<path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M11 19l-7-7 7-7m8 14l-7-7 7-7" />
						</svg>
						{!sidebarCollapsed && <span className="text-sm font-medium">Collapse menu</span>}
					</button>
				</div>
			</aside>

			<main
				className={`flex-1 min-h-screen transition-[margin] duration-300 ease-in-out ${sidebarCollapsed ? 'ml-20' : 'ml-72'}`}
			>
				<div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">{children}</div>
			</main>
		</div>
	);
};

export default SubscriptionSidebarLayout;
